import { omsdk } from '@paramount-streaming/blackbird';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';
import { TPluginConfigFactory } from 'tag/ts/tag/player/adapters/avia/types/TPluginConfigFactory';

/**
 * @copyright © Paramount Streaming
 * @description Wraps the OM SDK (OMID) plugin from Pluto TV Blackbird for SmartTag.
 * Wires Blackbird ad lifecycle events into an OMID session so that third-party
 * verification scripts can capture standardized viewability and verification signals.
 *
 * Disabled by default — enable via tag config (`OmSdk.enabled = true`, `OmSdk.params.enabled = true`).
 * `serviceUrl`, `sessionClientUrl`, `loaderUrl`, and `partnerVersion` are pre-resolved at runtime;
 * `partnerName` defaults to `"plutotv"`. `serviceUrl` (web Service) + `sessionClientUrl` (Session
 * Client) are the two-file path; `loaderUrl` is the legacy concatenated-file fallback.
 *
 * @see {@link https://playback-docs.webctv.plutostaging.tv/docs/plugins/omsdk}
 */
export class OmSdkPlugin extends AviaPlugin implements IAviaPlugin {
	package = omsdk as unknown as TPluginConfigFactory;
	namespaceKey = 'plugin_omsdk';

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'OmSdkPlugin'
			}
		);
	}

	/**
	 * @description Should be used if STITCHER params are present and OmSdk is enabled in tag config.
	 * @returns {boolean}
	 */
	shouldBeUsed() {
		return this.hasStitcherParams() && !!this.tag?.model?.config?.tag?.OmSdk?.enabled;
	}

	/**
	 * @description Returns parameters required to initialize omsdk from Pluto TV Blackbird.
	 * Reads from tag config (OmSdk.params) and maps to OmSdkPluginOptions:
	 * - omSdkConfig: core plugin config (enabled, serviceUrl, sessionClientUrl, loaderUrl, partnerName, etc.)
	 *   `serviceUrl`, `sessionClientUrl`, and `loaderUrl` are pre-resolved by TagModel from
	 *   `runtimeVO.url.root` if not set by the page, ensuring the scripts are always served from the
	 *   same CDN domain as Smart Tag itself. Blackbird's loader uses serviceUrl + sessionClientUrl.
	 * - getVideoElement: factory-level override for video element resolution
	 * - getCurrentContentUrl: factory-level override for OMID content URL
	 * @see {@link https://playback-docs.webctv.plutostaging.tv/docs/plugins/omsdk}
	 */
	getFactoryParams() {
		const params = this.tag?.model?.config?.tag?.OmSdk?.params;
		return {
			...super.getFactoryParams(),
			omSdkConfig: {
				...params,
				// IAS bundleId for Pluto TV web — differs from partnerName ("plutotv").
				// Can be overridden by host app via OmSdk.params.appId in tag config.
				appId: params?.appId ?? 'com.pluto.web',
				// Authenticated user ID for OMID verification — config override takes precedence,
				// falls back to GET_SESSION_DATA (populated once TN Web wires PTVWS-5495).
				ppid: params?.ppid ?? this.tag?.model?.siteUtilities?.siteSessionData?.ppid
			},
			getVideoElement: params?.getVideoElement,
			getCurrentContentUrl: params?.getCurrentContentUrl
		};
	}
}